import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";

import {
  Banner,
  BlockStack,
  Spinner,
  DropZone,
  Thumbnail,
  Text,
  List,
} from "@shopify/polaris";
import { NoteIcon } from "@shopify/polaris-icons";

import { useUpdateAdminTeamMutation } from "../../../services/api";

import { DirectUploadProvider } from "react-activestorage-provider";
import { isMobile } from "react-device-detect";

import FadeIn from "../../FadeIn";

const TeamLogoUpload = (props) => {
  const { teamId, isDeletingLogo } = props;

  const [updateAdminTeam, { isLoading }] = useUpdateAdminTeamMutation();

  const [file, setFile] = useState();
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const hasError = rejectedFiles.length > 0;
  const [fileUploadError, setFileUploadError] = useState(false);

  const validImageTypes = ["image/gif", "image/jpeg", "image/png"];

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles, handleUpload) => {
      setFileUploadError(false);
      setFile(acceptedFiles[0]);
      setRejectedFiles(_rejectedFiles);
      handleUpload(acceptedFiles);
    },
    []
  );

  const handleAttachment = (signedIds) => {
    return updateAdminTeam({ id: teamId, logo: signedIds[0] })
      .unwrap()
      .then(() => {
        setFile();
        return { status: "success" };
      })
      .catch((error) => {
        setFileUploadError(true);
        console.log(error);
      });
  };

  const fileUpload = !file && <DropZone.FileUpload actionHint=" or drop files to upload" />;
  const uploadedFiles = file && (
    <BlockStack>
      <BlockStack align="center" gap="200">
        <Thumbnail
          size="small"
          alt={file.name}
          source={
            validImageTypes.includes(file.type)
              ? window.URL.createObjectURL(file)
              : NoteIcon
          }
        />
        <div>
          {file.name} <Text variant="bodySm" as="p">{file.size} bytes</Text>
        </div>
      </BlockStack>
    </BlockStack>
  );

  const fileTypeErrorMessage = hasError && (
    <Banner title="The following files couldn't be uploaded:" tone="critical">
      <List type="bullet">
        {rejectedFiles.map((file, index) => (
          <List.Item key={index}>
            {`"${file.name}" is not supported. File type must be .gif, .png, or .jpg.`}
          </List.Item>
        ))}
      </List>
    </Banner>
  );

  const token = window.localStorage.getItem("access-token");
  const client = window.localStorage.getItem("client");
  const uid = window.localStorage.getItem("uid");

  return (
    <DirectUploadProvider
      multiple={false}
      onSuccess={handleAttachment}
      headers={{ "access-token": token, client: client, uid: uid }}
      render={({ handleUpload, uploads, ready }) => (
        <>
          {!isMobile && (
            <FadeIn fadeIn={isLoading || isDeletingLogo}>
              <Spinner accessibilityLabel="Spinner" size="large" />
            </FadeIn>
          )}

          {isMobile && (isLoading || isDeletingLogo) && (
            <FadeIn fadeIn={isLoading || isDeletingLogo}>
              <Spinner accessibilityLabel="Spinner" size="large" />
            </FadeIn>
          )}

          {!isLoading && (
            <BlockStack gap="200">
              {fileUploadError && !hasError && (
                <Banner tone="critical" title="Upload failed">
                  We were unable to upload the logo you provided due to a
                  system error. Please try again in a few minutes.
                </Banner>
              )}
              {fileTypeErrorMessage}
              <div style={{ width: 140, height: 100 }}>
                <DropZone
                  accept={[...validImageTypes]}
                  allowMultiple={false}
                  errorOverlayText="File type must be .gif, .png, or .jpg"
                  onDrop={(_dropFiles, acceptedFiles, _rejectedFiles) =>
                    handleDropZoneDrop(
                      _dropFiles,
                      acceptedFiles,
                      _rejectedFiles,
                      handleUpload
                    )
                  }
                >
                  {uploadedFiles}
                  {fileUpload}
                </DropZone>
              </div>
            </BlockStack>
          )}
        </>
      )}
    />
  );
};

TeamLogoUpload.propTypes = {
  teamId: PropTypes.string.isRequired,
  isDeletingLogo: PropTypes.bool,
};

export default TeamLogoUpload;
